import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { ConsentProvider, useConsent } from "@/context/ConsentContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import CookieConsent from "@/components/CookieConsent";
import WelcomeLocalePopup from "@/components/WelcomeLocalePopup";
import AppVersionNotifier from "@/components/AppVersionNotifier";
import { SUPPORTED_LOCALES, URL_TO_CODE, CODE_TO_URL, DEFAULT_LOCALE_URL } from "@/lib/i18nRouter";
import { initMetaPixel, trackPageView } from "@/lib/metaPixel";
import { captureMetaAttribution } from "@/lib/metaAttribution";
import { initTikTokPixel, trackTTPageView } from "@/lib/tiktokPixel";
import { initGA4, ga4PageView } from "@/lib/ga4";
import { startVersionWatcher } from "@/lib/appVersion";
import { reapStaleCaches } from "@/lib/swReap";

// Module-level constant so it's not re-created on every App render
// (re-creating breaks reference equality and causes Toaster to remount).
const TOASTER_OPTIONS = {
  style: {
    background: "#121212",
    border: "1px solid #D4AF37",
    color: "#fff",
    borderRadius: 0,
    fontFamily: "'Outfit', sans-serif",
  },
};

import Home from "@/pages/Home";
import Collection from "@/pages/Collection";
import GemstoneDetail from "@/pages/GemstoneDetail";
import AuctionHouse from "@/pages/AuctionHouse";
import AuctionDetail from "@/pages/AuctionDetail";
import CertificateVerify from "@/pages/CertificateVerify";
import VIP from "@/pages/VIP";
import Admin from "@/pages/Admin";
import { Login, Register } from "@/pages/Auth";
import { About, PrivateSales, SellGemstone, Concierge, InvestorRelations, Blog, Contact, InvestmentGems } from "@/pages/StaticPages";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Kyc from "@/pages/Kyc";
import FindYourStone from "@/pages/FindYourStone";
import { BirthstoneDetail, ZodiacDetail } from "@/pages/StoneDetails";
import Wishlist from "@/pages/Wishlist";
import Cart from "@/pages/Cart";
import PassportLanding from "@/pages/PassportLanding";
import ConsultationSuccess from "@/pages/ConsultationSuccess";
import ReviewSubmit from "@/pages/ReviewSubmit";
import ConsultationHistory from "@/pages/ConsultationHistory";
import {
  Authenticity,
  Certification,
  Warranty,
  FAQ,
  EthicalSourcing,
  Returns,
  Shipping,
  AuctionRules,
  Privacy,
  Terms,
  Disclaimer,
} from "@/pages/Policies";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const AnalyticsTracker = () => {
  const { pathname } = useLocation();
  const { consent } = useConsent();

  // Always hide the pre-React splash on first paint, regardless of consent.
  // Also kick off cache-reaping + the version watcher exactly once.
  useEffect(() => {
    const splash = document.getElementById("opik-splash");
    if (splash) {
      requestAnimationFrame(() => {
        splash.style.opacity = "0";
        setTimeout(() => { splash.remove(); }, 600);
      });
    }
    // Reap any leftover service workers / CacheStorage entries from older
    // deploys so subsequent fetches always hit the live origin.
    reapStaleCaches();
    // Begin polling /version.json so we can notify the user when a fresh
    // deploy goes out.
    startVersionWatcher();
  }, []);

  // Initialise scripts based on the user's current consent. These init helpers
  // are idempotent — calling them again after the user grants consent is safe.
  useEffect(() => {
    if (consent?.analytics) {
      initGA4();
    }
    if (consent?.marketing) {
      initMetaPixel();
      initTikTokPixel();
    }
    // Always capture attribution signals (Meta fbp/fbc) — these are essential
    // for the server-side CAPI Purchase events to attribute correctly. The
    // fbp cookie is set by the Pixel itself if loaded; we also persist fbclid
    // URL params so the click survives across the conversion path.
    try { captureMetaAttribution(); } catch { /* ignore */ }
  }, [consent?.analytics, consent?.marketing]);

  // Fire page views on every route change, gated by category.
  useEffect(() => {
    if (consent?.analytics) ga4PageView(pathname);
    if (consent?.marketing) {
      trackPageView();
      trackTTPageView();
    }
    // First-party server-side beacon (always on — anonymous, no cookies)
    // Powers the admin "Visitor Analytics" dashboard.
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      if (base) {
        fetch(`${base}/api/analytics/pv`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            path: pathname,
            referrer: document.referrer || "",
            lang: (navigator.language || "").slice(0, 8),
            title: (document.title || "").slice(0, 200),
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      /* never block navigation on telemetry */
    }
  }, [pathname, consent?.analytics, consent?.marketing]);

  return null;
};

/**
 * Single source of truth for URL ↔ LanguageContext sync.
 * - Reads first path segment.
 * - If valid locale: syncs to context.
 * - If not: redirects to /${currentLocale}${pathname}.
 * Runs invisibly.
 */
const LocaleSync = () => {
  const location = useLocation();
  const { lang: ctxLang, change } = useLang();
  const segments = location.pathname.split("/").filter(Boolean);
  const first = (segments[0] || "").toLowerCase();
  const valid = SUPPORTED_LOCALES.includes(first);

  useEffect(() => {
    if (valid) {
      const code = URL_TO_CODE[first];
      if (code && code !== ctxLang) change(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, first]);

  if (!valid) {
    const fallbackLocale = CODE_TO_URL[ctxLang] || DEFAULT_LOCALE_URL;
    const target = `/${fallbackLocale}${location.pathname === "/" ? "" : location.pathname}${location.search}${location.hash}`;
    return <Navigate to={target} replace />;
  }
  return null;
};

// Build a single route declaration set for all pages, mounted under /:lang/.
const PAGE_ROUTES = [
  { path: "collection", element: <Collection /> },
  { path: "gemstone/:id", element: <GemstoneDetail /> },
  { path: "auctions", element: <AuctionHouse /> },
  { path: "auctions/:id", element: <AuctionDetail /> },
  { path: "verify", element: <CertificateVerify /> },
  { path: "investment", element: <InvestmentGems /> },
  { path: "about", element: <About /> },
  { path: "private-sales", element: <PrivateSales /> },
  { path: "sell", element: <SellGemstone /> },
  { path: "concierge", element: <Concierge /> },
  { path: "investor-relations", element: <InvestorRelations /> },
  { path: "blog", element: <Blog /> },
  { path: "contact", element: <Contact /> },
  { path: "login", element: <Login /> },
  { path: "register", element: <Register /> },
  { path: "vip", element: <VIP /> },
  { path: "admin", element: <Admin /> },
  { path: "checkout/success", element: <CheckoutSuccess /> },
  { path: "kyc", element: <Kyc /> },
  { path: "find-your-stone", element: <FindYourStone /> },
  { path: "birthstone/:slug", element: <BirthstoneDetail /> },
  { path: "zodiac/:slug", element: <ZodiacDetail /> },
  { path: "wishlist", element: <Wishlist /> },
  { path: "cart", element: <Cart /> },
  { path: "passport/:code", element: <PassportLanding /> },
  { path: "consultation/success", element: <ConsultationSuccess /> },
  { path: "review/:token", element: <ReviewSubmit /> },
  { path: "consultations", element: <ConsultationHistory /> },
  // Trust & Policies
  { path: "policies/authenticity", element: <Authenticity /> },
  { path: "policies/certification", element: <Certification /> },
  { path: "policies/warranty", element: <Warranty /> },
  { path: "policies/faq", element: <FAQ /> },
  { path: "policies/ethical-sourcing", element: <EthicalSourcing /> },
  { path: "policies/returns", element: <Returns /> },
  { path: "policies/shipping", element: <Shipping /> },
  { path: "policies/auction-rules", element: <AuctionRules /> },
  { path: "policies/privacy", element: <Privacy /> },
  { path: "policies/terms", element: <Terms /> },
  { path: "policies/disclaimer", element: <Disclaimer /> },
];

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <CurrencyProvider>
            <CartProvider>
            <AuthProvider>
              <WishlistProvider>
                <ConsentProvider>
                  <LocaleSync />
                  <Header />
                  <ScrollTop />
                  <AnalyticsTracker />
                  <Routes>
                    <Route path="/:lang" element={<Home />} />
                    {PAGE_ROUTES.map((r) => (
                      <Route key={`l-${r.path}`} path={`/:lang/${r.path}`} element={r.element} />
                    ))}
                  </Routes>
                  <Footer />
                  <WhatsAppFab />
                  <CookieConsent />
                  <WelcomeLocalePopup />
                  <AppVersionNotifier />
                  <Toaster theme="dark" position="top-right" toastOptions={TOASTER_OPTIONS} />
                </ConsentProvider>
              </WishlistProvider>
            </AuthProvider>
            </CartProvider>
          </CurrencyProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
